import React from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import type { ToolbarOptions } from '../StudyListToolbar';
import type { FilterSortDisplayForm } from './useFilterSortDisplayForm';
import * as styles from './FilterFormContent.module.scss';
import {
  Button,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  IconList,
  MultiSelectComboBox,
  Popover,
  PopoverContent,
  PopoverTrigger,
  RadioGroup,
  RadioButton,
  Checkbox,
  Text,
} from 'components/core';
import { Inline, Stack } from 'components/layout';

type Props = {
  tags: string[];
  form: UseFormReturn<FilterSortDisplayForm>;
  isAwaitingOtherReviewer: boolean;
  reviewStage: ToolbarOptions['reviewStage'];
};

const FuzzySearchInfo = () => {
  return (
    <Popover strategy="fixed" closeOnEscape={true} forcePlacement>
      <PopoverTrigger>
        <Button
          iconOnly
          isNaked
          size="xs"
          variant="ghost"
          htmlType="button"
          aria-label="More info on fuzzy search"
          data-pendo-key="authors-fuzzy-search-info"
        >
          <Icon size="sm" icon={IconList.solid.faCircleInfo} />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={styles.learnMoreBox}
        aria-label="Learn more about fuzzy search"
      >
        <Text>
          Searches for author names that match a part of a keyword. Helpful if
          you have author names like &quot;Ngoyi Ngongo&quot;, &quot;de
          Jonge&quot;, &quot;St-Jean&quot; or &quot;O&apos;Neil&quot;.
        </Text>
      </PopoverContent>
    </Popover>
  );
};

export const FilterFormContent = ({
  tags,
  form,
  isAwaitingOtherReviewer,
  reviewStage,
}: Props) => {
  const { register, control, getValues } = form;
  const votesOptions =
    reviewStage === 'screening'
      ? { All: 'All votes', Yes: 'Yes', No: 'No', Maybe: 'Maybe' }
      : { All: 'All votes', Include: 'Included', Exclude: 'Excluded' };

  return (
    <Stack spacing={3}>
      <Controller
        control={control}
        name="filter.selectedTags"
        render={({ field }) => (
          <FormControl className={styles.FilterComboBox}>
            <FormLabel isBold={true}>Filter by tags</FormLabel>
            <MultiSelectComboBox
              hasFixedOptions={true}
              enableFiltering={true}
              placeholder="Search or select from list..."
              data={tags}
              value={field.value}
              onChange={(value) => field.onChange(value)}
              className={styles.ComboBoxInput}
              // ComboBoxList's FloatingFocusManager aria-hides the label while open (MOA-118)
              aria-label="Filter by tags"
            />
          </FormControl>
        )}
      />
      <Controller
        control={control}
        name="filter.keywords"
        render={({ field }) => (
          <FormControl className={styles.FilterComboBox}>
            <FormLabel isBold={true}>Filter by keyword</FormLabel>
            <MultiSelectComboBox
              value={field.value}
              onChange={(value) => field.onChange(value)}
              className={styles.ComboBoxInput}
              hasChevron={false}
              placeholder="Type and press enter..."
              // ComboBoxList's FloatingFocusManager aria-hides the label while open (MOA-118)
              aria-label="Filter by keyword"
            />
          </FormControl>
        )}
      />
      <div>
        <fieldset className={styles.Fieldset}>
          <Heading
            type="h6"
            component="legend"
            id="withinLegend"
            className={styles.Legend}
          >
            Within
          </Heading>
          <RadioGroup
            ariaLabelledBy="withinLegend"
            defaultValue={getValues('filter.within')}
            size="sm"
          >
            <RadioButton
              size="sm"
              value="title_and_abstract"
              {...register('filter.within')}
            >
              <Text component="span" size="sm">
                Title and abstract
              </Text>
            </RadioButton>
            <RadioButton
              size="sm"
              value="authors_exact"
              {...register('filter.within')}
            >
              <Text component="span" size="sm">
                Authors (Exact match)
              </Text>
            </RadioButton>
            <RadioButton
              size="sm"
              value="authors_fuzzy"
              {...register('filter.within')}
            >
              <Inline spacing={2}>
                <Text component="span" size="sm">
                  Authors (Fuzzy search)
                </Text>
                <FuzzySearchInfo />
              </Inline>
            </RadioButton>
            <RadioButton
              size="sm"
              value="everything"
              {...register('filter.within')}
            >
              <Text component="span" size="sm">
                All of the above
              </Text>
            </RadioButton>
          </RadioGroup>
        </fieldset>
      </div>
      <div>
        <fieldset className={styles.Fieldset}>
          <Heading type="h6" component="legend" className={styles.Legend}>
            Filter by full-text
          </Heading>
          <Stack spacing={1}>
            <Checkbox {...register('filter.has_full_text')}>
              Has full-text
            </Checkbox>
            <Checkbox {...register('filter.needs_full_text')}>
              Needs full-text
            </Checkbox>
          </Stack>
        </fieldset>
      </div>
      {isAwaitingOtherReviewer && (
        <div>
          <fieldset className={styles.Fieldset}>
            <Heading
              type="h6"
              component="legend"
              id="votesLegend"
              className={styles.Legend}
            >
              Votes
            </Heading>
            <RadioGroup
              ariaLabelledBy="votesLegend"
              defaultValue={getValues('filter.vote')}
              size="md"
            >
              {Object.keys(votesOptions).map((option) => (
                <RadioButton
                  {...register('filter.vote')}
                  key={option}
                  value={option}
                >
                  <Text component="span" size="sm">
                    {votesOptions[option as keyof typeof votesOptions]}
                  </Text>
                </RadioButton>
              ))}
            </RadioGroup>
          </fieldset>
        </div>
      )}
    </Stack>
  );
};
